import { FC, PropsWithChildren, useState } from 'react';
import { useIntl } from 'react-intl';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import { Divider, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { Button } from '@/designSystem/Button/Button';
import { HighlightReturn } from '@/Organisms/SearchBar/utils';
import { Subject } from '@/types/subject';

import Input from './Input/Input';
import InputGoogle from './Input/InputGoogle';
import messages from './messages';

import useStyles from './styles';

type Props = {
  onOptionSubjectChange: Function;
  subjectDatas: Subject[];
  onOptionLocationChange: Function;
  onGoogleInputChange?: Function;
  onSubjectChange?: Function;
  onButtonClick?: () => void;
  defaultSubject?: string;
  defaultAddress?: string;
  sortedSubjects?: HighlightReturn[][];
};

const SearchBar: FC<PropsWithChildren<Props>> = ({
  sortedSubjects,
  onOptionSubjectChange,
  subjectDatas,
  onOptionLocationChange,
  onGoogleInputChange,
  onSubjectChange,
  onButtonClick,
  defaultSubject = '',
  defaultAddress,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up('md'));
  const [inputValue, setInputValue] = useState(defaultSubject);

  return (
    <div className={classes.root} {...rest}>
      <Input
        preSelectedData={sortedSubjects}
        Icon={SearchIcon}
        placeholder={formatMessage(messages.searchPlaceholder)}
        ariaLabel={formatMessage(messages.searchAriaLabel)}
        onOptionChange={onOptionSubjectChange}
        onTextChange={onSubjectChange}
        datas={subjectDatas}
        inputValue={inputValue}
        data-testid="subjectSearch"
        setInputValue={(value: string) => {
          if (value === '') {
            onSubjectChange('');
          }

          setInputValue(value);
        }}
      />
      <div className={classes.divider}>
        <Divider orientation={isMobile ? 'horizontal' : 'vertical'} />
      </div>
      <InputGoogle
        Icon={LocationOnIcon}
        placeholder={formatMessage(messages.locationPlaceholder)}
        ariaLabel={formatMessage(messages.locationAriaLabel)}
        onOptionChange={onOptionLocationChange}
        onChange={onGoogleInputChange}
        defaultValue={defaultAddress}
        data-testid="locationSearch"
        suggestAllTypesOfAddress
      />
      {isMobile && (
        <div className={classes.divider}>
          <Divider orientation="horizontal" />
        </div>
      )}
      <div className={classes.button}>
        <Button
          data-testid="searchButton"
          variant="primary"
          onClick={onButtonClick}
          label={formatMessage(messages.search)}
          isFullWidth
        />
      </div>
    </div>
  );
};

export default SearchBar;
